import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
  alpha,
  IconButton,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  CloudUpload as CloudUploadIcon,
  Download as DownloadIcon,
  CheckCircle as CheckCircleIcon,
  InfoOutlined as InfoIcon,
  Close as CloseIcon,
  Store as StoreIcon,
  Inventory as InventoryIcon,
} from "@mui/icons-material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import PageHeader from "../../components/layout/PageHeader";
import { branchAPI } from "../../services/branchAPI";
import { salesPointAPI } from "../../services/salesPointAPI";
import { productSalesPointAPI } from "../../services/productSalesPointAPI";
import { useAuthStore } from "../../stores/authStore";
import { useRBAC } from "../../hooks/useRBAC";
import { tokens } from "../../theme";

interface ImportResultSummary {
  total: number;
  created: number;
  updated: number;
  skipped: number;
  failed: number;
  results: Array<{
    row: number;
    name: string;
    status: "created" | "updated" | "skipped" | "failed";
    reason?: string;
  }>;
}

const ProductImportPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const { canChangePosScope } = useRBAC();
  const canChangeScope = canChangePosScope();

  // Branch & SalesPoint selection
  const urlBranchId = searchParams.get("branchId") || "";
  const urlSalesPointId = searchParams.get("salesPointId") || "";

  const [selectedBranch, setSelectedBranch] = useState(
    urlBranchId || user?.branchId || ""
  );
  const [selectedSalesPoint, setSelectedSalesPoint] = useState(
    urlSalesPointId || user?.salesPointId || ""
  );

  const salesPointId = canChangeScope
    ? selectedSalesPoint
    : user?.salesPointId || "";

  // File upload state
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [importing, setImporting] = useState(false);
  const [alert, setAlert] = useState<{
    type: "success" | "error" | "info";
    message: string;
  } | null>(null);
  const [importResult, setImportResult] = useState<ImportResultSummary | null>(
    null
  );

  // Queries for branches & sales points
  const { data: branches = [] } = useQuery({
    queryKey: ["branches", "all"],
    queryFn: () => branchAPI.getAllBranches(),
    enabled: canChangeScope,
  });

  const { data: salesPoints = [] } = useQuery({
    queryKey: ["sales_points", "all"],
    queryFn: () => salesPointAPI.getAllSalesPoints(),
    enabled: canChangeScope,
  });

  const { data: userSalesPoint } = useQuery({
    queryKey: ["sales_point", salesPointId],
    queryFn: () => salesPointAPI.getSalesPointById(salesPointId),
    enabled: !!salesPointId && !canChangeScope,
  });

  const filteredSalesPoints = selectedBranch
    ? salesPoints.filter((sp) => sp.branchId === selectedBranch)
    : salesPoints;

  // Auto-select first sales point if none selected
  useEffect(() => {
    if (!selectedSalesPoint && filteredSalesPoints.length > 0) {
      setSelectedSalesPoint(filteredSalesPoints[0].id);
    }
  }, [selectedSalesPoint, filteredSalesPoints]);

  // Sync selected branch if salesPoint changes
  useEffect(() => {
    if (selectedSalesPoint && salesPoints.length) {
      const sp = salesPoints.find((s) => s.id === selectedSalesPoint);
      if (sp && sp.branchId && sp.branchId !== selectedBranch) {
        setSelectedBranch(sp.branchId);
      }
    }
  }, [selectedSalesPoint, salesPoints, selectedBranch]);

  // Download Shah Mastana Template
  const downloadTemplate = () => {
    // Header names must not contain commas — an unquoted comma splits one
    // column into several and shifts every data cell to the wrong column.
    const rows: (string | number)[][] = [
      [
        "Name",
        "Category",
        "Price",
        "GST% (0/5/12/18/28/40)",
        "Type (B=Inclusive / F=Exclusive)",
        "SKU",
        "Unit",
        "HSN",
        "ProductType",
        "PurchasePrice",
        "WorkCategories",
      ],
      ["Milk Badam", "Cold Beverages", 60, 5, "B", "SMD-0001", "PCS", "21069099", "PREPARED", 39, "restaurant"],
      ["Cold Coffee", "Cold Beverages", 50, 5, "B", "SMD-0576", "PCS", "21069099", "PREPARED", 32.5, "restaurant"],
      ["Veg Burger", "Fast Food", 70, 5, "B", "SMD-0102", "PCS", "21069099", "PREPARED", 45, "restaurant"],
      ["Mineral Water 1L", "Packaged Goods", 20, 18, "B", "SMD-0205", "PCS", "22011010", "GOODS", 15, "restaurant"],
    ];
    const escapeCsv = (v: string | number) => {
      const s = String(v);
      return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = rows.map((r) => r.map(escapeCsv).join(",")).join("\r\n") + "\r\n";

    // BOM so Excel opens the file as UTF-8
    const blob = new Blob(["﻿" + csv], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "products-import-sample.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  // Drag & drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) validateAndSetFile(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) validateAndSetFile(file);
    e.target.value = "";
  };

  const validateAndSetFile = (file: File) => {
    const ext = file.name.split(".").pop()?.toLowerCase();
    if (!["xlsx", "xls", "csv"].includes(ext || "")) {
      setAlert({
        type: "error",
        message: "Only Excel (.xlsx, .xls) and CSV (.csv) files are supported.",
      });
      return;
    }
    setSelectedFile(file);
    setAlert(null);
    setImportResult(null);
  };

  const handleUploadSubmit = async () => {
    if (!selectedFile) {
      setAlert({ type: "error", message: "Please select an Excel or CSV file." });
      return;
    }
    if (!salesPointId) {
      setAlert({
        type: "error",
        message: "Please select a target Sales Point (Department).",
      });
      return;
    }

    setImporting(true);
    setAlert(null);
    try {
      const summary = await productSalesPointAPI.importExcel(
        salesPointId,
        selectedFile
      );
      setImportResult(summary);
      await queryClient.invalidateQueries({
        queryKey: ["productSalesPointList"],
      });
      setAlert({
        type: "success",
        message: `Import completed: ${summary.created} added, ${summary.updated} updated, ${summary.failed} failed.`,
      });
    } catch (err: any) {
      setAlert({
        type: "error",
        message:
          err?.response?.data?.message ||
          err?.message ||
          "Import failed. Please verify column headers and data.",
      });
    } finally {
      setImporting(false);
    }
  };

  // Get current active sales point name
  const currentSalesPointName =
    salesPoints.find((sp) => sp.id === salesPointId)?.name ||
    userSalesPoint?.name ||
    "Selected Sales Point";

  return (
    <Box>
      <PageHeader
        title="Direct Product Import"
        subtitle="Upload Excel (.xlsx, .xls) or CSV files directly into the Department / Sales Point"
        breadcrumbs={[
          { label: "Dashboard", to: "/dashboard" },
          { label: "Product List", to: "/product-list" },
          { label: "Direct Import" },
        ]}
        action={
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => {
                const query = salesPointId
                  ? `?salesPointId=${encodeURIComponent(salesPointId)}&branchId=${encodeURIComponent(selectedBranch)}`
                  : "";
                navigate(`/product-list${query}`);
              }}
            >
              Back to Product List
            </Button>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={downloadTemplate}
            >
              Download Sample
            </Button>
          </Stack>
        }
      />

      {alert && (
        <Alert
          severity={alert.type}
          sx={{ mb: 3 }}
          onClose={() => setAlert(null)}
        >
          {alert.message}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Left Column: Scope & File Upload */}
        <Grid item xs={12} md={7}>
          <Card
            sx={{
              borderRadius: tokens.radius.md,
              border: "1px solid",
              borderColor: "divider",
              boxShadow: "none",
              mb: 3,
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                1. Select Destination Sales Point
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2.5}>
                Imported products will be mapped to the master catalog and made available in this Department for billing.
              </Typography>

              {canChangeScope ? (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Branch (Optional)</InputLabel>
                      <Select
                        value={selectedBranch}
                        label="Branch (Optional)"
                        onChange={(e) => {
                          setSelectedBranch(e.target.value);
                        }}
                      >
                        <MenuItem value="">
                          <em>All Branches / Direct Company</em>
                        </MenuItem>
                        {branches.map((b) => (
                          <MenuItem key={b.id} value={b.id}>
                            {b.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Sales Point (Department)</InputLabel>
                      <Select
                        value={selectedSalesPoint}
                        label="Sales Point (Department)"
                        onChange={(e) => setSelectedSalesPoint(e.target.value)}
                      >
                        {filteredSalesPoints.map((sp) => (
                          <MenuItem key={sp.id} value={sp.id}>
                            {sp.name} {sp.branch?.name ? `(${sp.branch.name})` : ""}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              ) : (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    bgcolor: alpha(tokens.colors.primary.main, 0.04),
                    borderColor: alpha(tokens.colors.primary.main, 0.2),
                    borderRadius: tokens.radius.sm,
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <StoreIcon color="primary" />
                    <Box>
                      <Typography variant="subtitle2" fontWeight={700}>
                        {currentSalesPointName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Assigned Department for current user session
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              )}

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" fontWeight={700} gutterBottom>
                2. Upload Excel / CSV File
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Supports <strong>.xlsx</strong>, <strong>.xls</strong>, and{" "}
                <strong>.csv</strong> files.
              </Typography>

              {/* Drag and Drop Box */}
              <Box
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  border: "2px dashed",
                  borderColor: isDragging
                    ? "primary.main"
                    : selectedFile
                    ? "success.main"
                    : "divider",
                  borderRadius: tokens.radius.md,
                  bgcolor: isDragging
                    ? alpha(tokens.colors.primary.main, 0.08)
                    : selectedFile
                    ? alpha(tokens.colors.success.main, 0.04)
                    : "background.default",
                  p: 4,
                  textAlign: "center",
                  cursor: "pointer",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    borderColor: "primary.main",
                    bgcolor: alpha(tokens.colors.primary.main, 0.04),
                  },
                }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  hidden
                  onChange={handleFileChange}
                />
                <CloudUploadIcon
                  sx={{
                    fontSize: 48,
                    color: selectedFile ? "success.main" : "primary.main",
                    mb: 1,
                  }}
                />
                <Typography variant="subtitle1" fontWeight={700}>
                  {selectedFile
                    ? selectedFile.name
                    : "Click to browse or drag & drop file here"}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {selectedFile
                    ? `${(selectedFile.size / 1024).toFixed(1)} KB`
                    : "Excel (.xlsx, .xls) or CSV (.csv)"}
                </Typography>
              </Box>

              {selectedFile && (
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    mt: 2,
                    p: 1.5,
                    borderRadius: tokens.radius.sm,
                    bgcolor: alpha(tokens.colors.success.main, 0.08),
                    border: "1px solid",
                    borderColor: alpha(tokens.colors.success.main, 0.3),
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CheckCircleIcon color="success" fontSize="small" />
                    <Typography variant="body2" fontWeight={600}>
                      {selectedFile.name} (
                      {(selectedFile.size / 1024).toFixed(1)} KB)
                    </Typography>
                  </Stack>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedFile(null);
                      setImportResult(null);
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Stack>
              )}

              <Box mt={3} display="flex" justifyContent="flex-end">
                <Button
                  variant="contained"
                  size="large"
                  disabled={!selectedFile || !salesPointId || importing}
                  onClick={handleUploadSubmit}
                  startIcon={
                    importing ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <CloudUploadIcon />
                    )
                  }
                  sx={{ minWidth: 200 }}
                >
                  {importing ? "Importing Products…" : "Start Direct Import"}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column: Template Guide & Format Info */}
        <Grid item xs={12} md={5}>
          <Card
            sx={{
              borderRadius: tokens.radius.md,
              border: "1px solid",
              borderColor: "divider",
              boxShadow: "none",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
                <InfoIcon color="primary" />
                <Typography variant="h6" fontWeight={700}>
                  Template Specifications
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Recommended file format and column structure:
              </Typography>

              <Stack spacing={1.5}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: tokens.radius.sm,
                    bgcolor: "background.default",
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography variant="caption" fontWeight={700} color="text.primary">
                    Required Columns
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    • <strong>Name</strong> (Product title)<br />
                    • <strong>Category</strong> (Created if not exists)<br />
                    • <strong>Price</strong> (Selling price for this department)<br />
                    • <strong>GST%</strong> (Allowed: 0, 5, 12, 18, 28, 40)<br />
                    • <strong>Type</strong> (<strong>B</strong> = Tax Inclusive / Backward, <strong>F</strong> = Tax Exclusive / Forward)
                  </Typography>
                </Box>

                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: tokens.radius.sm,
                    bgcolor: "background.default",
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography variant="caption" fontWeight={700} color="text.primary">
                    Optional Columns
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    • <strong>SKU</strong> / Item Code (Auto-generated if blank)<br />
                    • <strong>Unit</strong> (Default: PCS, KG, LTR, PACK, BOX)<br />
                    • <strong>HSN</strong> (e.g. 21069099)<br />
                    • <strong>ProductType</strong> (PREPARED or GOODS)<br />
                    • <strong>PurchasePrice</strong> (Cost price, auto 65% fallback)<br />
                    • <strong>WorkCategories</strong> (e.g. restaurant)
                  </Typography>
                </Box>
              </Stack>

              <Box mt={3}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={downloadTemplate}
                >
                  Download Sample
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* IMPORT RESULTS SECTION */}
      {importResult && (
        <Box mt={4}>
          <Typography variant="h6" fontWeight={700} gutterBottom>
            Import Results Summary
          </Typography>

          <Grid container spacing={2} mb={3}>
            <Grid item xs={6} sm={3}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  textAlign: "center",
                  borderRadius: tokens.radius.sm,
                }}
              >
                <Typography variant="h4" fontWeight={800} color="text.primary">
                  {importResult.total}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Total Processed
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  textAlign: "center",
                  borderRadius: tokens.radius.sm,
                  bgcolor: alpha(tokens.colors.success.main, 0.05),
                  borderColor: alpha(tokens.colors.success.main, 0.3),
                }}
              >
                <Typography variant="h4" fontWeight={800} color="success.main">
                  {importResult.created}
                </Typography>
                <Typography variant="caption" color="success.main" fontWeight={600}>
                  New Products Added
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  textAlign: "center",
                  borderRadius: tokens.radius.sm,
                  bgcolor: alpha(tokens.colors.primary.main, 0.05),
                  borderColor: alpha(tokens.colors.primary.main, 0.3),
                }}
              >
                <Typography variant="h4" fontWeight={800} color="primary.main">
                  {importResult.updated}
                </Typography>
                <Typography variant="caption" color="primary.main" fontWeight={600}>
                  Existing Updated
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  textAlign: "center",
                  borderRadius: tokens.radius.sm,
                  bgcolor:
                    importResult.failed > 0
                      ? alpha(tokens.colors.error.main, 0.05)
                      : "background.paper",
                  borderColor:
                    importResult.failed > 0
                      ? alpha(tokens.colors.error.main, 0.3)
                      : "divider",
                }}
              >
                <Typography
                  variant="h4"
                  fontWeight={800}
                  color={importResult.failed > 0 ? "error.main" : "text.secondary"}
                >
                  {importResult.failed}
                </Typography>
                <Typography
                  variant="caption"
                  color={
                    importResult.failed > 0 ? "error.main" : "text.secondary"
                  }
                  fontWeight={600}
                >
                  Failed Rows
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          <Card
            sx={{
              borderRadius: tokens.radius.md,
              border: "1px solid",
              borderColor: "divider",
              boxShadow: "none",
            }}
          >
            <Box
              sx={{
                px: 2.5,
                py: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography variant="subtitle1" fontWeight={700}>
                Item Details ({importResult.results.length} rows)
              </Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={<InventoryIcon />}
                onClick={() => {
                  const query = salesPointId
                    ? `?salesPointId=${encodeURIComponent(salesPointId)}&branchId=${encodeURIComponent(selectedBranch)}`
                    : "";
                  navigate(`/product-list${query}`);
                }}
              >
                View in Product List
              </Button>
            </Box>
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, width: 80 }}>Row</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Item Name</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: 120 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Message / Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {importResult.results.map((r, i) => (
                    <TableRow key={i} hover>
                      <TableCell>{r.row}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{r.name}</TableCell>
                      <TableCell>
                        <Chip
                          label={r.status.toUpperCase()}
                          size="small"
                          sx={{
                            fontWeight: 700,
                            fontSize: "0.7rem",
                            bgcolor:
                              r.status === "created"
                                ? tokens.colors.success.light
                                : r.status === "updated"
                                ? alpha(tokens.colors.primary.main, 0.1)
                                : tokens.colors.error.light,
                            color:
                              r.status === "created"
                                ? tokens.colors.success.main
                                : r.status === "updated"
                                ? "primary.main"
                                : tokens.colors.error.main,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {r.reason || (r.status === "created" ? "Assigned to sales point" : "Price updated")}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Box>
      )}
    </Box>
  );
};

export default ProductImportPage;
